import { captureException } from "src/lib/monitoring";
import immutableUpdate from "immutability-helper";

import {
  type AndCondition,
  type AndOrCondition,
  type Condition,
  ConditionType,
  createPropertyCondition,
  DefaultOperators,
  type EventCondition,
  type FunnelCondition,
  getInitialTraitColumn,
  type NumberOfCondition,
  type OrCondition,
  type PropertyCondition,
  type NestableCondition,
  type TraitCondition,
  type InlineTraitCondition,
} from "src/types/visual";

import type { ConditionWithSubconditions } from "./format-subconditions";
import { isAndOrCondition } from "./type-guards";
import { getTraitPropertyTypeFromTraitDefinition } from "src/components/explore/visual/utils";
import type { TraitDefinitionWithoutFilterableColumnsFragment } from "src/graphql";
import type {
  Updater,
  UpdaterAdvanced,
  UpdaterEnforceDefined,
} from "src/utils/immutable-helpers";
import type { Draft } from "immer";

export type ParentCondition =
  | AndCondition<AndOrCondition<Condition>>
  | OrCondition<AndOrCondition<Condition>>;

export const updateConditionAtIndex = (
  index: number,
  conditions: PropertyCondition[],
  updates: Partial<PropertyCondition>,
) => {
  if (conditions.length < index || !conditions[index]) {
    return conditions;
  }

  return immutableUpdate(conditions, { [index]: { $merge: updates } });
};

/** Mutates condition, assumes immer-based state */
export const groupSubcondition = (
  index: number,
  condition: ParentCondition,
) => {
  const subcondition = condition.conditions[index];
  if (!subcondition) {
    return;
  }

  const newGroup: AndOrCondition<Condition> = {
    type:
      condition.type === ConditionType.And
        ? ConditionType.Or
        : ConditionType.And,
    id: crypto.randomUUID(),
    conditions: [subcondition],
  };

  condition.conditions[index] = newGroup;
};

/** Mutates condition, assumes immer-based state */
export const ungroupSubcondition = (
  index: number,
  condition: ParentCondition,
) => {
  const groupToUnwrap = condition.conditions[
    index
  ] as AndOrCondition<Condition>;

  if (!isAndOrCondition(groupToUnwrap)) {
    return;
  }

  condition.conditions.splice(index, 1, ...groupToUnwrap.conditions);
};

export type OnUpdate<C extends Condition | FunnelCondition> = (
  updater: UpdaterAdvanced<Condition | FunnelCondition, C>,
) => void;

/** Nearly the same as `createSubconditionUpdater` except operates on `conditions` instead of `subconditions` */
export const createChildConditionUpdater =
  (
    onUpdate: (
      updater: Updater<AndCondition<Condition> | OrCondition<Condition>>,
    ) => void,
  ) =>
  (id?: string) =>
  <ChildCondition extends NestableCondition>(
    childUpdater: UpdaterAdvanced<Condition | FunnelCondition, ChildCondition>,
  ) => {
    if (!id) {
      captureException(new Error("Condition in query builder is missing id"));
      return;
    }

    onUpdate((draft) => {
      if (!draft.conditions) {
        return;
      }
      const index = draft.conditions.findIndex((c) => c.id === id);
      if (index === -1) {
        return;
      }
      const result = childUpdater(
        draft.conditions[index]! as Draft<ChildCondition>,
      );

      if (result) {
        // We are intentionally overwriting with a different condition type, we support this
        // through the exposed types
        draft.conditions[index] = result as any;
      }
    });
  };

export const createSubconditionUpdater =
  // Parent condition should only include `Condition` types that have `subconditions` as their children key
  // conditions with `conditions` as the child key go in `createChildConditionUpdater`
  <
      ParentCondition extends
        | EventCondition
        | NumberOfCondition
        | PropertyCondition
        | FunnelCondition,
    >(
      onUpdate:
        | OnUpdate<ParentCondition>
        | ((updater: UpdaterEnforceDefined<ParentCondition>) => void),
      args?: { disableSorting?: boolean },
    ) =>
    (id?: string) =>
    <ChildCondition extends NestableCondition>(
      childUpdater: UpdaterAdvanced<
        Condition | FunnelCondition,
        ChildCondition
      >,
    ) => {
      if (!id) {
        captureException(new Error("Condition in query builder is missing id"));
        return;
      }

      onUpdate((draft) => {
        if (!draft.subconditions) {
          return;
        }
        const topLevelSubcondition = draft
          .subconditions[0] as AndOrCondition<ChildCondition>;
        if (!topLevelSubcondition || !isAndOrCondition(topLevelSubcondition)) {
          return;
        }
        const index = topLevelSubcondition.conditions.findIndex(
          (c) => c.id === id,
        );
        if (index === -1) {
          return;
        }
        const result = childUpdater(
          topLevelSubcondition.conditions[index]! as Draft<ChildCondition>,
        );
        if (result) {
          // We are intentionally overwriting with a different condition type, we support this
          // through the exposed types
          topLevelSubcondition.conditions[index] = result as any;
        }

        if (!args?.disableSorting) {
          // For aesthetic purposes only, apply subcondition sorting in case a condition type changed
          topLevelSubcondition.conditions.sort(sortSubconditions);
        }
      });
    };

export const createTraitSubconditionUpdater =
  <ParentCondition extends TraitCondition | InlineTraitCondition>(
    onUpdate:
      | OnUpdate<ParentCondition>
      | ((updater: UpdaterEnforceDefined<ParentCondition>) => void),
  ) =>
  (id?: string) =>
  <ChildCondition extends NestableCondition>(
    childUpdater: UpdaterAdvanced<Condition | FunnelCondition, ChildCondition>,
  ) => {
    if (!id) {
      captureException(new Error("Condition in query builder is missing id"));
      return;
    }

    onUpdate((draft) => {
      if (
        !(
          "conditions" in draft.property.column &&
          draft.property.column.conditions
        )
      ) {
        return;
      }
      const topLevelSubcondition = draft.property.column
        .conditions[0] as AndOrCondition<ChildCondition>;
      if (!topLevelSubcondition || !isAndOrCondition(topLevelSubcondition)) {
        return;
      }
      const index = topLevelSubcondition.conditions.findIndex(
        (c) => c.id === id,
      );
      if (index === -1) {
        return;
      }
      const result = childUpdater(
        topLevelSubcondition.conditions[index]! as Draft<ChildCondition>,
      );
      if (result) {
        // We are intentionally overwriting with a different condition type, we support this
        // through the exposed types
        topLevelSubcondition.conditions[index] = result as any;
      }

      // For aesthetic purposes only, always apply subcondition sorting in case a condition type changed
      topLevelSubcondition.conditions.sort(sortSubconditions);
    });
  };

export function removeSubconditionMutative(
  condition: PropertyCondition,
  id?: string,
): void;
export function removeSubconditionMutative(
  condition: EventCondition,
  id?: string,
): void;
export function removeSubconditionMutative(
  condition: NumberOfCondition,
  id?: string,
): void;
export function removeSubconditionMutative(
  condition: FunnelCondition,
  id?: string,
): void;
export function removeSubconditionMutative(
  condition: ConditionWithSubconditions,
  id?: string,
): void {
  if (!id) {
    const error = new Error("Tried to remove subcondition but id was missing");

    captureException(error, {
      extra: {
        condition: JSON.stringify(condition),
      },
    });

    return;
  }

  if (
    !condition.subconditions ||
    !isAndOrCondition(condition.subconditions?.[0])
  ) {
    // No wrapping and/or condition
    condition.subconditions = [
      {
        type: ConditionType.And,
        id: crypto.randomUUID(),
        conditions: (condition.subconditions ?? []).filter(
          (subcondition) => subcondition.id !== id,
        ),
      },
    ];
    return;
  }

  if (condition.subconditions.length > 1) {
    // There are multiple and/or conditions. This should not happen.
    const error = new Error(
      "Event condition with invalid subconditions detected",
    );

    captureException(error, {
      extra: {
        condition: JSON.stringify(condition),
      },
    });

    return;
  }

  const topLevelSubcondition = condition.subconditions[0];
  condition.subconditions = [
    {
      type: topLevelSubcondition.type,
      id: topLevelSubcondition.id,
      conditions: topLevelSubcondition.conditions.filter(
        (subcondition) => subcondition.id !== id,
      ),
    },
  ];
}

export const createNewTraitSubcondition = (
  trait: TraitDefinitionWithoutFilterableColumnsFragment,
): PropertyCondition => {
  const propertyType = getTraitPropertyTypeFromTraitDefinition(trait);

  return {
    ...createPropertyCondition(),
    type: ConditionType.Property,
    propertyType,
    property: getInitialTraitColumn(trait),
    propertyOptions: {
      traitType: "trait",
    },
    operator: DefaultOperators[propertyType] ?? null,
    value: null,
  };
};

/** Places the big subconditions (related, event) after small (property) */
export const sortSubconditions = (a: Condition, b: Condition) => {
  if (a.type === b.type) {
    return 0;
  }
  if (a.type === ConditionType.Property) {
    return -1;
  }
  if (b.type === ConditionType.Property) {
    return 1;
  }
  return 0;
};
